import React, { useState } from "react";
import { object, string, number, date, InferType } from 'yup';

import apiService from "../../../services/ApiService";
import TailwindSelect from "../../Tailwind/TailwindSelect";

import snakeToPascal from "../../../tools";
import { useSaveIndicator } from "../../../hooks/useSaveIndicator";
import SaveIndicator from "../../SaveIndicator";
import { useEditorStore } from "../../../shared/stores/useEditorStore";
import { useQueryClient } from "@tanstack/react-query";
import { SECTION_DATA_KEY } from "../../../shared/hooks/queries/useSectionLoader";


export default function SensitivityTimingReferenceField({ name, item, field, currentSensitivity }) {
    const isReadOnly = !useEditorStore(s => s.userRights.right_to_write);
    const scenarioId = useEditorStore(s => s.scenarioId) ?? 0;
    const queryClient = useQueryClient();

    const invalidateQueries = () => {
        // Only invalidate section-data cache — do NOT invalidate SCENARIO_TREE_KEY (see useUpdateFieldValue.ts)
        queryClient.invalidateQueries({ queryKey: [SECTION_DATA_KEY, scenarioId] });
    };

    const sensitivityField = item.sensitivityModelData.fields[field.name];

    const originalValue = (field.currentValue.value) ? field.currentValue.value : "null";
    const initialValue = (sensitivityField) ? sensitivityField.value.value : originalValue

    const [fieldValue, setFieldValue] = React.useState(initialValue as string);
    const { status: saveStatus, trigger: triggerSave, resolve: resolveSave } = useSaveIndicator();
    const [potentialRelations, setPotentialRelations] = React.useState([]);
    const [optionsLoading, setOptionsLoading] = React.useState(true);

    React.useEffect(() => {
        setOptionsLoading(true);
        apiService.getTimingOptions(field.id).then(async (response) => {
            if (response) {
                const data = await response.json();
                setPotentialRelations(data);

                // Set current relation by id
                if (fieldValue !== 'null' && typeof fieldValue === 'number') {
                    const relation = data.find((e: any) => e.id == fieldValue);
                    if (relation) setFieldValue(relation);
                }
            }
            setOptionsLoading(false);
        });
    }, [field.id]);

    const handleChange = (value) => {
        const apiValue = (value !== 'null') ? value.id : null;

        triggerSave();
        if (sensitivityField) {
            apiService.putSensitivityValue(sensitivityField.id, apiValue).then(
                async(_response) => {
                    resolveSave();
                    invalidateQueries();
                }
            )
        }
        else {

            apiService.upsertSensitivityValue(item.sensitivityModelData.id, field.name, field.type, null).then(
                async(response) => {
                    if (response) {
                        const newSensitivityField = await response.json()

                        item.sensitivityModelData.fields[field.name] = newSensitivityField

                        apiService.putSensitivityValue(newSensitivityField.id, apiValue).then(
                            async(_response) => {
                                resolveSave();
                                invalidateQueries();
                            }
                        )
                    }
                }
            )
        }
        setFieldValue(value);
    }

    const resetSensitivityValue = () => {
        apiService.deleteSensitivityValue(sensitivityField.id).then(
            async(response) => {
                if (response) {
                    delete item.sensitivityModelData.fields[field.name];
                    setFieldValue(originalValue)
                    invalidateQueries();
                }
            }
        )
    }

    // Build options for TailwindSelect
    const selectOptions = [
        {
            value: 'null',
            label: snakeToPascal(name),
            isItalic: true
        },
        ...potentialRelations.map((e: any) => ({
            value: e,
            label: `${e.parent_name} ${snakeToPascal(e.field_name)}`
        }))
    ];

    return (
        <div className="flex flex-row items-center">
            {!isReadOnly && (
                <button
                    onClick={resetSensitivityValue}
                    disabled={!sensitivityField}
                    className="mr-2 h-10 w-10 flex items-center justify-center text-slate-600 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    <span className="material-symbols-outlined text-[30px]">restart_alt</span>
                </button>
            )}
            <div className="w-[200px]">
                <TailwindSelect
                    value={fieldValue}
                    onChange={handleChange}
                    options={selectOptions}
                    placeholder={snakeToPascal(name)}
                    disabled={isReadOnly || optionsLoading}
                />
            </div>
            <SaveIndicator status={saveStatus} className="ml-2" />
        </div>
    )
}
